import styled from "styled-components";
import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import {
    BreadcrumbsCommon,
    FilterItemComponent,
    ClipLoaderCommon,
    SortMenuComponent,
    PageHead,
} from "@components";
import { AppModuleLayout } from "@layouts";
import {
    breakpoint,
    theme,
    useWindowDimensions,
    getSiteMeta,
    buildBreadcrumbJsonLd,
} from "@utilities";
import { Z_INDEX } from "@constants";
import { FilterModule, PaginationModule, SearchTourElementModule } from "@module";
import { PATH_HOME, PATH_SEARCH_RESULT } from "@routes";
import {
    AppDispatch,
    clearListTour,
    searchQuery,
    selectSearchCategories,
    selectSearchTour,
    setCurrentPage,
    setName,
    setSortBy,
    setSortDirection,
    setRegions,
    setCountries,
    useAppSelector,
} from "@redux";
import { IOptionSearch, IPagination, sortDirection, SortBy } from "@interfaces";
import {
    PAGINATION,
    SORT_NAME_ASC,
    SORT_NAME_DESC,
    SORT_PRICE_ASC,
    SORT_PRICE_DESC,
} from "@constants";
import { useLocation, useNavigate } from "react-router-dom";
import _ from "lodash";

const FILTER_TITLE = "Filter";
const SORT_BY_TITLE = "Sort by";

const LOADING_MESSAGES = [
    "Fetching your next adventure... are you ready!",
    "We make memorable holidays",
    "Your dream trip is just moments away",
    "Exploring the best experiences for you",
    "Adventure awaits around every corner",
    "Life is short, the world is wide",
    "Finding hidden gems just for you",
];

const SearchLoadingMessage = () => {
    const [msgIndex, setMsgIndex] = useState(0);
    const [fade, setFade] = useState(true);

    useEffect(() => {
        const interval = setInterval(() => {
            setFade(false);
            setTimeout(() => {
                setMsgIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
                setFade(true);
            }, 400);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    return (
        <LoadingContainer>
            <ClipLoaderCommon color={theme.colors.highlight} size={50} margin={0} />
            <LoadingText $visible={fade}>{LOADING_MESSAGES[msgIndex]}</LoadingText>
        </LoadingContainer>
    );
};

export const SearchResult = () => {
    //hook
    const dispatch = useDispatch<AppDispatch>();
    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const searchTerm = queryParams.get("query");
    const isDestinationExplore = queryParams.get("destinationExplore") === "true";
    const regionIdFromUrl = queryParams.get("regions");
    const countryIdFromUrl = queryParams.get("countries");
    const navigate = useNavigate();
    // Redux state
    const searchTours = useAppSelector(selectSearchTour);
    const searchCategories = useAppSelector(selectSearchCategories);
    const sortBy = searchCategories.sortBy;
    const sortDirection = searchCategories.sortDirection;
    const listResults = searchTours.listTours;
    // page state
    const [nameValue, setNameValue] = useState<string>(searchCategories.name || "");
    useEffect(() => {
        setNameValue(searchCategories.name || "");
    }, [searchCategories.name]);
    const [isShowSortMenu, setIsShowSortMenu] = useState<boolean>(false);
    const [isShowFilter, setIsShowFilter] = useState<boolean>(false);
    const [isFilterExpanded, setIsFilterExpanded] = useState<boolean>(false);
    const [optionSort, setOptionSort] = useState<sortDirection>("asc");
    const [sortTitle, setSortTitle] = useState<string>(SORT_BY_TITLE);
    const [listPagePoints, setListPagePoints] = useState<IPagination[]>([]);

    //page variable
    const { width } = useWindowDimensions();
    const isLoadMore = useRef(false);
    const listTabSearch = [
        {
            title: "Search by Keyword",
            icon: "/images/fi_search.png",
        },
    ];
    const breadcrumbsData = [
        {
            title: "Home",
            link: PATH_HOME,
        },
        {
            title: "Search result",
            link: PATH_SEARCH_RESULT,
        },
    ];

    const optionSearch: IOptionSearch = {
        "records-start": (searchTours.currentPage - 1) * PAGINATION,
        "records-length": PAGINATION,
        sortDirection: "asc",
    };

    const getDisplayPrice = (faresprices: any[]) => {
        const nonZero = (faresprices || []).filter((fp: any) => fp.rrp > 0);
        const faresToUse = nonZero.length > 0 ? nonZero : faresprices;
        const prices = _.map(faresToUse, "rrp");
        const hasVariablePricing = _.uniq(prices).length > 1;
        return hasVariablePricing ? _.min(prices) : prices[0];
    };

    const sortedListResults = [...listResults].sort((a, b) => {
        if (sortBy === "rrp") {
            const priceA = getDisplayPrice(a.faresprices ?? []);
            const priceB = getDisplayPrice(b.faresprices ?? []);
            return sortDirection === "asc" ? priceA - priceB : priceB - priceA;
        }
        // default: no sorting for name handled by backend
        return 0;
    });

    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        if (isDestinationExplore && regionIdFromUrl && countryIdFromUrl) {
            dispatch(setRegions({ id: regionIdFromUrl, text: "" }));
            dispatch(setCountries({ id: countryIdFromUrl, text: "" }));
        } else {
            dispatch(setName(searchTerm || ""));
        }
        setIsInitialized(true);
    }, [searchTerm, isDestinationExplore, regionIdFromUrl, countryIdFromUrl]);

    useEffect(() => {
        const listPagePointsNew = [];
        if (searchTours.totalRecord > PAGINATION) {
            const totalPage = Math.ceil(searchTours.totalRecord / PAGINATION);
            for (let i = 1; i <= totalPage; i++) {
                const pagePoint = {
                    id: i,
                };
                listPagePointsNew.push(pagePoint);
            }
        }
        setListPagePoints(listPagePointsNew);

        if (sortBy === "name") {
            setSortTitle(sortDirection === "asc" ? SORT_NAME_ASC : SORT_NAME_DESC);
        } else if (sortBy === "rrp") {
            setSortTitle(sortDirection === "asc" ? SORT_PRICE_ASC : SORT_PRICE_DESC);
        } else {
            setSortTitle(SORT_PRICE_ASC);
        }
    }, [searchTours, sortBy, sortDirection]);

    const hasAnyFilterSelected = () => {
        if (isDestinationExplore && regionIdFromUrl && countryIdFromUrl) return true;

        const hasName = !!searchCategories.name?.trim();
        const hasAccommodation = (searchCategories.accommodation?.length ?? 0) > 0;
        const hasActivities = (searchCategories.activities?.length ?? 0) > 0;
        const hasSuppliers = (searchCategories.suppliers?.length ?? 0) > 0;
        const hasTransports = (searchCategories.transports?.length ?? 0) > 0;
        const hasDuration = !!searchCategories.duration;
        const hasDestination = (searchCategories.destinations?.length ?? 0) > 0;
        const hasStartLocation = (searchCategories.startLocations?.length ?? 0) > 0;

        return hasName || hasAccommodation || hasActivities || hasSuppliers || 
               hasTransports || hasDuration || hasDestination || hasStartLocation;
    };

    const noFiltersSelected = !hasAnyFilterSelected();
    const showNoFilterMessage = noFiltersSelected;

    const activeFilterLabels = (() => {
        const labels: string[] = [];
        if (searchCategories.suppliers?.length > 0)
            labels.push(...searchCategories.suppliers.map((s: any) => s.text));
        if (searchCategories.accommodation?.length > 0)
            labels.push(...searchCategories.accommodation.map((a: any) => a.text));
        if (searchCategories.activities?.length > 0)
            labels.push(...searchCategories.activities.map((a: any) => a.text));
        if (searchCategories.transports?.length > 0)
            labels.push(...searchCategories.transports.map((t: any) => t.text));
        if (searchCategories.destinations?.length > 0)
            labels.push(...searchCategories.destinations.map((d: any) => d.text));
        if (searchCategories.startLocations?.length > 0)
            labels.push(...searchCategories.startLocations.map((l: any) => l.text));
        if (searchCategories.duration)
            labels.push(typeof searchCategories.duration === "string" ? searchCategories.duration : (searchCategories.duration as any)?.text || "");
        return labels;
    })();

    useEffect(() => {
        if (!isInitialized) return;
        if (!hasAnyFilterSelected()) return;

        optionSearch.sortBy = sortBy;
        optionSearch.sortDirection = sortDirection;
        dispatch(searchQuery(isLoadMore.current, optionSearch, isDestinationExplore));
    }, [searchTours.currentPage, sortBy, sortDirection, isDestinationExplore, isInitialized]);

    const suppliersRef = useRef(searchCategories.suppliers);
    useEffect(() => {
        const prev = suppliersRef.current;
        suppliersRef.current = searchCategories.suppliers;
        if (!isInitialized) return;
        if (prev === searchCategories.suppliers) return;
        if ((searchCategories.suppliers?.length ?? 0) === 0) return;
        dispatch(clearListTour());
        dispatch(searchQuery(false, { ...optionSearch, sortBy, sortDirection }, isDestinationExplore));
    }, [searchCategories.suppliers]);

    const handleToggleShowMenu = (title: string) => {
        if (title === SORT_BY_TITLE) {
            setIsShowSortMenu(!isShowSortMenu);
        } else if (title === FILTER_TITLE) {
            setIsShowFilter(!isShowFilter);
        }
    };

    const handleSetNameValue = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.currentTarget.value;
        setNameValue(newValue);
        const newPath = `${PATH_SEARCH_RESULT}?query=${encodeURIComponent(newValue)}`;
        navigate(newPath);
    };

    const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleApplyFilter();
        }
    };

    const handleApplyFilter = () => {
        dispatch(setName(nameValue));
        if (!hasAnyFilterSelected() && !nameValue?.trim()) return;
        dispatch(searchQuery(false, optionSearch, isDestinationExplore));
        dispatch(clearListTour());
        isLoadMore.current = false;
    };

    const handleSetCurrentPage = (page: number) => {
        dispatch(setCurrentPage(page));
    };

    const handleSort = (sort: SortBy, id: number) => {
        if (sort.key === "Name") {
            dispatch(setSortBy("name"));
            dispatch(setSortDirection(sort.value[id].value));
        }
        if (sort.key === "Price") {
            dispatch(setSortBy("rrp"));
            dispatch(setSortDirection(sort.value[id].value));
        }
        if (optionSort === sort.value[id].value) {
            setOptionSort(null);
            setSortTitle(sort.value[id].title);
            dispatch(setCurrentPage(1));
        } else {
            setOptionSort(sort.value[id].value);
            setSortTitle(sort.value[id].title);
            dispatch(setCurrentPage(1));
        }
        isLoadMore.current = false;
    };

    const handleLoadMore = () => {
        isLoadMore.current = true;
        handleSetCurrentPage(searchTours.currentPage + 1);
    };
    const handleBlur = () => {
        handleApplyFilter();
    };

    return (
        <AppModuleLayout>
            <PageHead
                seo={{
                    title: `Search Tours & Trips | ${theme.title}`,
                    metaDescription: `Search and compare tours, trips and travel experiences with ${theme.title}.`,
                    structuredDataType: "WebPage",
                }}
                jsonLd={(() => {
                    const site = getSiteMeta();
                    return [
                        buildBreadcrumbJsonLd(
                            [
                                { name: "Home", url: site.siteUrl || "/" },
                                {
                                    name: "Search results",
                                    url: site.siteUrl
                                        ? `${site.siteUrl}${PATH_SEARCH_RESULT}`
                                        : PATH_SEARCH_RESULT,
                                },
                            ],
                            site,
                        ),
                    ];
                })()}
            />
            {width < 767 ? (
                <SearchResultMobile>
                    <Container>
                        <FilterItemComponent
                            title="Search"
                            listTabs={listTabSearch}
                            nameValue={nameValue}
                            onSetNameValue={handleSetNameValue}
                            onkeyup={handleEnter}
                            onBlur={handleBlur}
                        />
                        <FilterMenu isShowFilter={isShowFilter}>
                            <FilterItem
                                onClick={() => {
                                    handleToggleShowMenu(FILTER_TITLE);
                                }}
                            >
                                <img src="/images/fi_sliders.png" alt={FILTER_TITLE} />
                                <span>{FILTER_TITLE}</span>
                            </FilterItem>

                            <FilterItem
                                onClick={() => {
                                    handleToggleShowMenu(SORT_BY_TITLE);
                                }}
                            >
                                {isShowSortMenu && <div className="overlay"></div>}
                                <img src="/images/fi_chevron-down.png" alt="fi_2chevrons-down" />
                                Sort
                                <SortMenuComponent
                                    onSort={handleSort}
                                    isShowSortMenu={isShowSortMenu}
                                    titleSort={sortTitle}
                                />
                            </FilterItem>
                            <div className="filter">
                                <FilterModule
                                    onToggleShowFilter={() => handleToggleShowMenu(FILTER_TITLE)}
                                    onApplyFilter={handleApplyFilter}
                                />
                            </div>
                        </FilterMenu>

                        <MobileResultCount>
                            {searchTours.loading ? "---" : searchTours.totalRecord} Tours Found
                        </MobileResultCount>

                        <ListSearchResultMobile>
                            {showNoFilterMessage ? (
                                <p className="notFoundTour">
                                    Please enter a keyword or select a filter to search for tours.
                                </p>
                            ) : searchTours.loading && !isLoadMore.current ? (
                                <SearchLoadingMessage />
                            ) : searchTours.totalRecord === 0 ? (
                                <p className="notFoundTour">
                                    Sorry, no results found... Try changing your keywords or
                                    clearing your filters
                                </p>
                            ) : (
                                <>
                                    <MobileVerticalGrid>
                                        {sortedListResults.map((tour) => (
                                            <SearchTourElementModule
                                                data={tour}
                                                key={tour.productId}
                                                variant="grid-card"
                                            />
                                        ))}
                                    </MobileVerticalGrid>
                                    {searchTours.currentPage <
                                        Math.ceil(searchTours.totalRecord / PAGINATION) && (
                                        <MobileLoadMoreButton onClick={searchTours.loading ? undefined : handleLoadMore}>
                                            {searchTours.loading ? (
                                                <ClipLoaderCommon
                                                    color={theme.colors.highlight}
                                                    size={24}
                                                    margin={0}
                                                />
                                            ) : (
                                                "Load More"
                                            )}
                                        </MobileLoadMoreButton>
                                    )}
                                </>
                            )}
                        </ListSearchResultMobile>
                    </Container>
                </SearchResultMobile>
            ) : (
                <DesktopSearchResult $isShowSortMenu={isShowSortMenu}>
                    <div className="breadcrumbs-row">
                        <BreadcrumbsCommon data={breadcrumbsData} />
                    </div>
                    <CollapsibleFilterBar $isExpanded={isFilterExpanded}>
                        <FilterBarSearchRow>
                            <div className="searchRow">
                                <div className="searchInput">
                                    <FilterItemComponent
                                        title="Search"
                                        listTabs={listTabSearch}
                                        nameValue={nameValue}
                                        onSetNameValue={handleSetNameValue}
                                        onkeyup={handleEnter}
                                        onBlur={handleBlur}
                                    />
                                </div>
                                <div className="searchActions">
                                    <SearchBarApplyButton
                                        onClick={handleApplyFilter}
                                    >
                                        Apply Filter
                                    </SearchBarApplyButton>
                                </div>
                            </div>
                        </FilterBarSearchRow>
                        <FilterBarHeader onClick={() => setIsFilterExpanded(!isFilterExpanded)}>
                            <FilterBarToggle>
                                <img src="/images/fi_sliders.png" alt="Filter" />
                                <span>Advanced Filters</span>
                                {activeFilterLabels.length > 0 && (
                                    <ActiveFilterBadge>
                                        Filtered: {activeFilterLabels.join(", ")}
                                    </ActiveFilterBadge>
                                )}
                            </FilterBarToggle>
                            <FilterBarChevron $isExpanded={isFilterExpanded}>&#9660;</FilterBarChevron>
                        </FilterBarHeader>
                        <FilterBarContent $isExpanded={isFilterExpanded}>
                            <FilterBarFilters>
                                <FilterModule
                                    onApplyFilter={handleApplyFilter}
                                    onResetSearch={() => {
                                        setNameValue("");
                                        dispatch(clearListTour());
                                    }}
                                />
                            </FilterBarFilters>
                        </FilterBarContent>
                    </CollapsibleFilterBar>
                    <div className="result-full">
                        <div className="summary">
                            <div className="numberOfResult">
                                {searchTours.loading ? "---" : searchTours.totalRecord} Tours
                                Found
                            </div>
                            <div
                                className="sortBy"
                                onClick={() => {
                                    handleToggleShowMenu(SORT_BY_TITLE);
                                }}
                            >
                                {isShowSortMenu && <div className="overlay"></div>}
                                <img
                                    src="/images/fi_2chevrons-down.png"
                                    alt="fi_2chevrons-down"
                                />
                                Sort
                                <SortMenuComponent
                                    onSort={handleSort}
                                    isShowSortMenu={isShowSortMenu}
                                    titleSort={sortTitle}
                                />
                            </div>
                        </div>
                        <div className="listSearchResult">
                            {showNoFilterMessage ? (
                                <p className="notFoundTour">
                                    Please enter a keyword or select a filter to search for tours.
                                </p>
                            ) : searchTours.loading ? (
                                <SearchLoadingMessage />
                            ) : searchTours.totalRecord === 0 ? (
                                <p className="notFoundTour">
                                    Sorry, no results found... Try changing your keywords or
                                    clearing your filters
                                </p>
                            ) : (
                                <DesktopGrid>
                                    {sortedListResults.map((tour) => (
                                        <SearchTourElementModule
                                            data={tour}
                                            key={tour.productId}
                                            variant="grid-card"
                                        />
                                    ))}
                                </DesktopGrid>
                            )}
                        </div>
                        <div className="pagination">
                            {searchTours.totalRecord > PAGINATION && !searchTours.loading && (
                                <PaginationModule
                                    onSetCurrentPage={handleSetCurrentPage}
                                    listPagePoints={listPagePoints}
                                    currentPage={searchTours.currentPage}
                                />
                            )}
                        </div>
                    </div>
                </DesktopSearchResult>
            )}
        </AppModuleLayout>
    );
};

const SearchResultMobile = styled.div`
    position: relative;
    min-height: calc(100vh - 95px);

    ${breakpoint.breakTablet`
        display: none;
    `}
`;

const Container = styled.div`
    padding: 26px 16px 80px 16px;
    background-color: #f2f2f2;
    min-height: calc(100vh - 95px);
`;

const FilterMenu = styled.div<{ isShowFilter: boolean }>`
    display: flex;
    justify-content: space-between;

    .filter {
        transition: 300ms;
        transform: ${(p) => (p.isShowFilter ? "translateX(0)" : "translateX(100%)")};
        position: fixed;
        top: 90px;
        left: 0;
        width: 100vw;
        height: calc(100vh - 90px);
        z-index: ${Z_INDEX.MODAL_CONTENT};
        max-height: calc(100vh - 90px);
        overflow-y: auto;
        background-color: #fff;
    }
`;

const FilterItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    position: relative;
    cursor: pointer;

    span {
        font-size: 16px;
        font-weight: 700;
        margin-left: 10px;
    }

    .overlay {
        background-color: transparent;
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 2;
    }
`;


const MobileResultCount = styled.div`
    font-weight: 700;
    font-size: 16px;
    margin-bottom: 12px;
`;

const ListSearchResultMobile = styled.div`
    min-height: 40vh;
    .notFoundTour {
        text-align: center !important;
        font-size: 18px;
        font-weight: 700;
    }
`;

const MobileVerticalGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    padding-bottom: 16px;
`;

const MobileLoadMoreButton = styled.button`
    width: 100%;
    padding: 14px;
    margin-bottom: 16px;
    background: #fff;
    border: 2px solid ${({ theme }) => theme.colors.highlight || "#e0e0e0"};
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.highlight || "#333"};
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &:active {
        opacity: 0.7;
    }
`;


const DesktopGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;

    ${breakpoint.breakIpadPro`
        grid-template-columns: repeat(3, 1fr);
    `}

    ${breakpoint.breakLarge`
        grid-template-columns: repeat(4, 1fr);
    `}
`;

const DesktopSearchResult = styled.div<{ $isShowSortMenu: boolean }>`
    display: none;
    background-color: #f2f2f2;
    margin-bottom: -40px;
    min-height: calc(100vh - 95px);

    ${breakpoint.breakTablet`
        display: block;
    `}

    .breadcrumbs-row {
        padding: 20px 0 0;
        margin: 0 auto;
        width: ${(props) => props.theme.widths.wide_700};

        ${breakpoint.breakIpadPro`
            width: ${(props: any) => props.theme.widths.wide_900};
        `}

        ${breakpoint.breakLarge`
            width: ${(props: any) => props.theme.widths.wide_1110};
        `}
    }

    .result-full {
        margin: 0 auto;
        width: ${(props) => props.theme.widths.wide_700};
        min-height: 800px;

        ${breakpoint.breakIpadPro`
            width: ${(props: any) => props.theme.widths.wide_900};
        `}

        ${breakpoint.breakLarge`
            width: ${(props: any) => props.theme.widths.wide_1110};
        `}

        .summary {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;
            margin-top: 5px;

            ${breakpoint.breakLarge`
                margin-bottom: 16px;
                margin-top: 16px;
            `}

            .numberOfResult {
                font-weight: 700;
                font-size: 19px;
            }

            .sortBy {
                display: flex;
                align-items: center;
                font-weight: 700;
                font-size: 14px;
                cursor: pointer;
                position: relative;

                img {
                    margin-right: 6px;
                }

                .overlay {
                    background-color: transparent;
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100vw;
                    height: 100vh;
                    z-index: 2;
                }
            }
        }

        .listSearchResult {
            margin-bottom: 40px;
        }

        .notFoundTour {
            text-align: center;
            font-weight: 700;
            font-size: 20px;
            padding-top: 12px;
        }
    }

    .sortMenu {
        position: absolute;
        display: ${(p) => (p.$isShowSortMenu ? "block" : "none")};
        top: 100%;
        left: 0;
        width: 100%;
        z-index: 2;
        overflow: hidden;
        box-shadow: 8px 22px 35px rgba(0, 0, 0, 0.16);

        .menuItem {
            cursor: pointer;
            font-size: 20px;
            text-align: center;
            height: 25px;
            line-height: 25px;
            z-index: 2;
            font-weight: 400;

            ${breakpoint.breakTablet`
                font-size: 14px;
            `}

            ${breakpoint.breakIpadPro`
                font-size: 16px;
            `}

            ${breakpoint.breakLarge`
                font-size: 18px;
            `}
        }
    }
`;

const CollapsibleFilterBar = styled.div<{ $isExpanded: boolean }>`
    margin: 8px auto 0;
    width: ${(props) => props.theme.widths.wide_700};
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    overflow: hidden;

    ${breakpoint.breakIpadPro`
        width: ${(props: any) => props.theme.widths.wide_900};
    `}

    ${breakpoint.breakLarge`
        width: ${(props: any) => props.theme.widths.wide_1110};
    `}
`;

const FilterBarHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    cursor: pointer;
    user-select: none;
    border-top: 1px solid #eee;

    &:hover {
        background: #fafafa;
    }
`;

const FilterBarToggle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    img {
        width: 16px;
        height: 16px;
    }

    span {
        font-weight: 600;
        font-size: 13px;
        color: #666;
    }
`;

const FilterBarChevron = styled.span<{ $isExpanded: boolean }>`
    font-size: 12px;
    color: #888;
    transition: transform 0.25s ease;
    transform: rotate(${(p) => (p.$isExpanded ? "180deg" : "0deg")});
`;

const ActiveFilterBadge = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${theme.colors.highlight};
    background: ${theme.colors.highlight}15;
    padding: 2px 10px;
    border-radius: 999px;
    margin-left: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 400px;
`;

const FilterBarContent = styled.div<{ $isExpanded: boolean }>`
    max-height: ${(p) => (p.$isExpanded ? "none" : "0")};
    opacity: ${(p) => (p.$isExpanded ? 1 : 0)};
    overflow: ${(p) => (p.$isExpanded ? "visible" : "hidden")};
    transition: opacity 0.25s ease;
`;

const FilterBarSearchRow = styled.div`
    padding: 10px 16px 6px;

    .searchRow {
        display: flex;
        align-items: stretch;
        gap: 12px;
    }

    .searchInput {
        flex: 4;
        min-width: 0;

        > div {
            margin: 0 !important;

            .searchResultPage .heading {
                padding: 0 0 4px;
                .text {
                    font-size: 14px;
                    line-height: 18px;
                }
            }

            .filterRow {
                padding: 0 !important;
            }
        }
    }

    .searchActions {
        flex: 1;
        display: flex;
        flex-direction: row;
        gap: 6px;
        align-items: center;
        min-width: 120px;
    }
`;

const FilterBarFilters = styled.div`
    padding: 0 16px 10px;

    > div {
        margin: 0 !important;
    }
`;

const SearchBarApplyButton = styled.button`
    flex: 3;
    background: linear-gradient(90deg, ${theme.gradients.button[0]}, ${theme.gradients.button[1]});
    color: ${theme.colors.black};
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
    padding: 8px 12px;

    &:hover {
        opacity: 0.9;
    }
`;


const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 20px 60px;
    gap: 24px;
`;

const LoadingText = styled.p<{ $visible: boolean }>`
    font-size: 18px;
    font-weight: 500;
    color: #555;
    text-align: center;
    opacity: ${(p) => (p.$visible ? 1 : 0)};
    transition: opacity 0.4s ease;
    min-height: 28px;
    margin: 0;

    ${breakpoint.breakIpadPro`
        font-size: 20px;
    `}
`;
